import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import PageLayout from "examples/LayoutContainers/PageLayout";
import Footer from "layouts/authentication/components/Footer";
import Card from "@mui/material/Card";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { Link } from "react-router-dom";
import { Phone, Email, Info, Image } from '@mui/icons-material';
import { useState, useEffect } from "react";
import { Modal, IconButton, Fade, Box, ButtonGroup, Button, CircularProgress } from "@mui/material";
import adimage from "assets/images/fall_2025_ad_two.png";
import adpdf from "assets/images/ad2.pdf"; 
import ZoomableImage from "components/ZoomableImage";
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { toast } from 'react-toastify'; // Assuming you're using react-toastify

// You'll need to define these constants or import them
const BASE_URL = process.env.REACT_APP_BASE_URL; // Adjust based on your auth implementation

function BasicLayout({ image, children }) {
  const [showImageModal, setShowImageModal] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  
  // Dynamic settings state
  const [email, setEmail] = useState("portal@bzu.edu.pk"); // Default fallback
  const [numbers, setNumbers] = useState(["+92-345-7964626", "+92-334-7774748", "+92-300-7004959"]); // Default fallback
  const [fileBase64, setFileBase64] = useState(adpdf); // Default fallback
  const [adImageUrl, setAdImageUrl] = useState(adimage); // For the modal image
  const [isLoading, setIsLoading] = useState(true);

  const handleOpenImageModal = () => setShowImageModal(true);
  const handleCloseImageModal = () => setShowImageModal(false);

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.2, 3));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.2, 1));

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartPos({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - startPos.x,
      y: e.clientY - startPos.y,
    });
  };

  const handleMouseUp = () => setIsDragging(false);

  // Load settings from API
  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${BASE_URL}/applicant/get_settings_public`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();

      // Update email
      if (data.email) {
        setEmail(data.email);
      }

      // Update phone numbers
      if (data.phones) {
        try {
          let phones = data.phones.replace(/'/g, '"') || [];
          const parsedNumbers = JSON.parse(phones);
          if (parsedNumbers.length > 0) {
            setNumbers(parsedNumbers);
          }
        } catch (parseError) {
          console.error("Error parsing phone numbers:", parseError);
        }
      }

      // Update advertisement URL
      if (data.ad_url) {
        setFileBase64(data.ad_url);
        // If the ad_url is also used for the modal image
        setAdImageUrl(data.ad_url);
      }

    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Failed to load settings. Using default values.");
    } finally {
      setIsLoading(false);
    }
  };

  // Load settings on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  // Support Info Card Component
  const SupportInfoCard = ({ isLoginPage = true }) => (
    <Card
      sx={{
        margin: "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: 3,
        height: "100%",
      }}
    >
      <MDBox
        variant="gradient"
        bgColor="info"
        borderRadius="lg"
        coloredShadow="info"
        mx={2}
        mt={-5}
        p={2}
        mb={3}
        textAlign="center"
      >
        <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
          Need Help?
        </MDTypography>
      </MDBox>
      
      {isLoading ? (
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </MDBox>
      ) : (
        <MDBox display="flex" flexDirection="column" gap={3}>
          <MDBox display="flex" alignItems="center">
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="3rem"
              height="3rem"
              bgColor="info"
              borderRadius="lg"
              shadow="md"
              mr={2}
            >
              <Phone style={{ color: "white" }} />
            </MDBox>
            <MDBox>
              <MDTypography variant="h6" fontWeight="medium">
                Support Helpline (WhatsApp Only)
              </MDTypography>
              <MDTypography variant="button" color="text">
                {numbers.map((number, index) => (
                  <span key={index}>
                    <a href={`https://wa.me/${number.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer">
                      {number}
                    </a>
                    {index < numbers.length - 1 && <br />}
                  </span>
                ))}
              </MDTypography>
            </MDBox>
          </MDBox>
          
          <MDBox display="flex" alignItems="center">
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="3rem"
              height="3rem"
              bgColor="info"
              borderRadius="lg"
              shadow="md"
              mr={2}
            >
              <Email style={{ color: "white" }} />
            </MDBox>
            <MDBox>
              <MDTypography variant="h6" fontWeight="medium">
                Support Email
              </MDTypography>
              <MDTypography variant="button" color="text">
                <a href={`mailto:${email}`} target="_blank" rel="noopener noreferrer">
                  {email}
                </a>
              </MDTypography>
            </MDBox>
          </MDBox>
          
          <MDBox display="flex" alignItems="center">
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="3rem"
              height="3rem"
              bgColor="info"
              borderRadius="lg"
              shadow="md"
              mr={2}
            >
              <Info style={{ color: "white" }} />
            </MDBox>
            <MDBox>
              <MDTypography variant="h6" fontWeight="medium">
                Working Hours
              </MDTypography>
              <MDTypography variant="button" color="text">
                Mon - Fri, 8:00 AM - 4:00 PM
              </MDTypography>
            </MDBox>
          </MDBox>
          
          <MDBox mt={1}>
            <MDTypography variant="h6" fontWeight="medium" mb={2} textAlign="center">
              How to Apply?
            </MDTypography>
            <MDButton
              variant="contained"
              color="info"
              onClick={() => window.open("https://web.facebook.com/share/v/1AVCroxb7h/", "_blank")}
              startIcon={<Image />}
              fullWidth
            >
              See Video Tutorial
            </MDButton>
          </MDBox>
          
          <MDBox mt={1}>
            <MDTypography variant="h6" fontWeight="medium" mb={2} textAlign="center">
              Want to see the advertisement?
            </MDTypography>
            <MDButton
              variant="contained"
              color="info"
              onClick={() => window.open(fileBase64, "_blank")}
              fullWidth
            >
              View Advertisement
            </MDButton>
          </MDBox>
          
          <MDBox mt={1}>
            {isLoginPage ? (
              <>
                <MDTypography variant="h6" fontWeight="medium" mb={2} textAlign="center">
                  New Applicant?
                </MDTypography>
                <MDButton
                  component={Link}
                  to="/student-register"
                  variant="gradient"
                  color="info"
                  fullWidth
                >
                  Create Account
                </MDButton>
                <MDTypography variant="h6" fontWeight="medium" mb={2} mt={2} textAlign="center">
                  Forgot Password?
                </MDTypography>
                <MDButton
                  component={Link}
                  to="/forgot-password"
                  variant="gradient"
                  color="info"
                  fullWidth
                >
                  Reset Password
                </MDButton>
              </>
            ) : (
              <>
                <MDTypography variant="h6" fontWeight="medium" mb={2} textAlign="center">
                  Already have an account?
                </MDTypography>
                <MDButton
                  component={Link}
                  to="/"
                  variant="gradient"
                  color="info"
                  fullWidth
                >
                  Sign In
                </MDButton>
              </>
            )}
          </MDBox>
        </MDBox>
      )}
    </Card>
  );

  return (
    <PageLayout>
      <MDBox
        position="absolute"
        width="100%"
        minHeight="100vh"
        sx={{
          backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
            image &&
            `${linearGradient(
              rgba(gradients.dark.main, 0.6),
              rgba(gradients.dark.state, 0.6)
            )}, url(${image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      
      {/* Campus Image Modal */}
      <Modal
        open={showImageModal}
        onClose={handleCloseImageModal}
        closeAfterTransition
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Fade in={showImageModal}>
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 2,
              boxShadow: 24,
              p: 2,
              maxWidth: '90%',
              maxHeight: '90%',
              position: 'relative',
              outline: 'none',
              overflow: 'hidden',
            }}
          >
            {/* Close Button */}
            <IconButton
              onClick={handleCloseImageModal}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'grey.500',
                zIndex: 10,
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Zoom Controls */}
            <ButtonGroup
              sx={{
                position: 'absolute',
                bottom: 16,
                right: 16,
                zIndex: 10,
              }}
            >
              <Button onClick={handleZoomIn} variant="contained" style={{color:'white'}}><ZoomInIcon /></Button>
              <Button onClick={handleZoomOut} variant="contained" style={{color:'white'}}><ZoomOutIcon /></Button>
            </ButtonGroup>

            {/* Image */}
            <Box
              component="img"
              src={adImageUrl}
              alt="Campaign Ad View"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              sx={{
                cursor: isDragging ? 'grabbing' : 'grab',
                transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                transformOrigin: 'center',
                transition: isDragging ? 'none' : 'transform 0.2s ease',
                maxWidth: '100%',
                maxHeight: '90vh',
                objectFit: 'contain',
                userSelect: 'none',
              }}
              draggable={false}
            />
          </Box>
        </Fade>
      </Modal>
      
      <MDBox px={1} width="100%" height="100vh" mx="auto">
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          {/* Main content */}
          <Grid item xs={12} sm={10} md={5} lg={5} xl={4}>
            {children}
          </Grid>

          {/* Support info card */}
          <Grid item xs={12} sm={10} md={4} lg={4} xl={4}>
            <SupportInfoCard isLoginPage={true} />
          </Grid>
        </Grid>
      </MDBox>
    </PageLayout>
  );
}

BasicLayout.propTypes = {
  image: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export default BasicLayout;